import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { messages } from './messages';
import { getSellerLabelProviderNameOverride } from './seller-provider-list/provider-mapping-service';
import { getIsProviderLocalDelivery } from '../../hooks/use-filtered-local-delivery-providers';
import { getCachedNormalizedBYOLProviders } from '../../hooks/use-normalized-BYOL-providers';
import Global from '../../store/global-selectors';
import { selectCountryFeatureConfig } from '../../store/country/country.selectors';
import getDisplayInformation from '../data-driven-logo/get-display-information';
import { LogService } from '../../services/log-service';
import { CountryConfigNames } from '../../contexts/country-config-context/country-config-enums';

const getSellerServiceProviderName = (sellerLabelProvider, intl) => {
  const { labelProviderId, accountNumber } = sellerLabelProvider;
  const nameOverride = getSellerLabelProviderNameOverride(sellerLabelProvider);
  if (nameOverride) {
    return nameOverride;
  }

  const displayInfo = getDisplayInformation({
    providerId: Number.parseInt(labelProviderId, 10),
  });
  if (!displayInfo) {
    LogService.logError(new Error('Failed to get seller label provider in rate viewer'), {
      sellerLabelProvider,
    });
  }
  const labelProviderName = displayInfo?.name ?? intl.formatMessage(messages.unknownLabelProviderLabel);
  if (accountNumber) {
    return intl.formatMessage(messages.unnamedProviderDisplayName, {
      labelProviderName,
      accountNumber,
    });
  }

  return labelProviderName;
};

const getSellerLabelProviderDisplayName = (sellerLabelProvider, intl) => {
  const { labelProviderId, name: accountName, accountNumber } = sellerLabelProvider;

  const nameOverride = getSellerLabelProviderNameOverride(sellerLabelProvider);
  if (nameOverride) {
    return nameOverride;
  }

  if (accountName) {
    return accountName;
  }

  const { name: labelProviderName } =
    getDisplayInformation({
      providerId: Number.parseInt(labelProviderId, 10),
    }) || {};

  if (accountNumber) {
    return intl.formatMessage(messages.unnamedProviderDisplayName, {
      labelProviderName,
      accountNumber,
    });
  }

  return labelProviderName;
};

export const filteredSellerLabelProvidersSelector = createSelector(
  Global.selectSellerLabelProviders,
  (_, { hideLocalDelivery }) => hideLocalDelivery,
  (_, { intl }) => intl,
  (state) => selectCountryFeatureConfig(state, CountryConfigNames.RateBrowserConfiguration),
  (sellerLabelProviders, hideLocalDelivery, intl, rateBrowserConfiguration) => {
    const cachedNormalizedBYOLProviders = getCachedNormalizedBYOLProviders();

    const [, rateBrowserConfigData] = rateBrowserConfiguration;
    const { showCarriersWithoutRates } = rateBrowserConfigData || {};

    return sellerLabelProviders.reduce((acc, sellerLabelProvider) => {
      // filter out BYOL
      if (cachedNormalizedBYOLProviders[sellerLabelProvider.labelProviderId]) {
        return acc;
      }

      // filter out local delivery, if necessary
      if (hideLocalDelivery && getIsProviderLocalDelivery(sellerLabelProvider.labelProviderId)) {
        return acc;
      }

      // filter out inactive providers
      if (sellerLabelProvider.status !== 'Active') {
        return acc;
      }

      if (!showCarriersWithoutRates && !sellerLabelProvider.sendRates) {
        return acc;
      }

      const displayName = getSellerLabelProviderDisplayName(sellerLabelProvider, intl);
      const nonAccountName = getSellerServiceProviderName(sellerLabelProvider, intl);

      acc.push({
        ...sellerLabelProvider,
        name: displayName,
        nonAccountName,
      });

      return acc;
    }, []);
  },
);

export const useRateViewerLabelProviders = (hideLocalDelivery) => {
  const intl = useIntl();
  return useSelector((state) => {
    return filteredSellerLabelProvidersSelector(state, {
      hideLocalDelivery,
      intl,
    });
  });
};
